import React, { useEffect, useState } from 'react'
import { ArrowLeftOutlined, LoadingOutlined, CloseOutlined, InboxOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { Button, Checkbox, ConfigProvider, Dropdown, InputNumber, Modal, Spin, Tag, Tooltip, message } from 'antd'
import { useLocation, useNavigate } from 'react-router-dom'
import { BackButton, DashboardColumn, DashboardItemWrapper, DashboardWrapper, Description, EmptyPlaceholder, LoadingWrapper, UpdatedAtWrapper, WidthSettingWrapper } from './dashboard.styled'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { fetchDashboardSetting, modDashboardSettingPosition, refreshDashboardData } from '../../apis/dashboard'
import { DashboardCategory, DashboardSettingType, DashboardSources } from '../../constants/DashboardConf'
import Profile from './charts/Profile'
import PIIComponent from './charts/PII'
import Footprint from './charts/Footprint'
import Summary from './charts/Summary'
import PieChart from './charts/PieChart'
import ColumnChart from './charts/ColumnChart'
import { fetchDataSource } from '../../apis/brand'
import SettingModal from './SettingModal'
import TaiwanMap from './charts/map/TaiwanMap'
import dayjs from 'dayjs'
import SiteChart from './charts/SiteChart/SiteChart'
import SankeyChart from './charts/Sankey'
import { config } from './Conf'
import LineChart from './charts/LineChart'
import ChartHeader from './charts/ChartHeader'
import { fetchSources } from '../../apis/source'

const { CONSUMER, BRAND, GROUP, SEGMENT } = DashboardCategory
const { PII, PROFILE, PIECHART, BARCHART, LINECHART, FOOTPRINT, SUMMARY, MAP, SANKEY, TIMELINE, CUSTOM, TAGS } = DashboardSettingType
const { FIRSTPARTY_TAXONOMY, THIRDPARTY_TAXONOMY } = DashboardSources
const minColWidth = 200
const initColWidth = 300

function DashboardSetting (props) {
  const { goBackPath, adminMode, type, fixedData, noHeader, noDragAndDrop, content, setupContentFunc = () => {}, useFakeData, setting, useSetting, renderBasedOnStatus, allowRefresh, setContent = () => {} } = props
  const navigate = useNavigate()
  const { t: ln_t } = useTranslation()
  const location = useLocation()
  const { state } = location || {}
  const { data = fixedData, prevSetting, prevWidth = {} } = state || {}
  const initCol = type === CONSUMER ? { 1: {}, 2: {}, 3: {} } : { 1: {}, 2: {} }

  const [isLoading, setIsLoading] = useState(false)
  const [refreshLoading, setRefreshLoading] = useState(false)
  const [columns, setColumns] = useState(initCol)
  const [dataSrcList, setDataSrcList] = useState([])
  const [dataSrcTypes, setDataSrcTypes] = useState([])
  const [showModal, setShowModal] = useState(false)
  const [modalData, setModalData] = useState({})

  const conf = {
    [CONSUMER]: { title: ln_t('consumer.consumer360'), desc: `${ln_t('dashboard.brand')}: ${data?.name}, ${ln_t('dashboard.group')}: ${data?.group?.name}` },
    [BRAND]: { title: ln_t('dashboard.brandDashboard'), desc: `${ln_t('dashboard.brand')}: ${data?.name}, ${ln_t('dashboard.group')}: ${data?.group?.name}` },
    [GROUP]: { title: ln_t('dashboard.groupDashboard'), desc: `${ln_t('dashboard.group')}: ${data?.group?.name}` },
    [SEGMENT]: { title: ln_t('dashboard.segmentPreview'), desc: `${ln_t('dashboard.brand')}: ${data?.name}, ${ln_t('dashboard.group')}: ${data?.group?.name}` }
  }

  const goBack = () => navigate(goBackPath, { state: { page: state?.page } })
  const handleSaveView = async () => {
    setIsLoading(true)
    const reqBody = []
    Object.entries(columns).forEach(([id, col]) => {
      const { width, autoWidth, items } = col || {}
      items.forEach((i, idx) => {
        reqBody.push({ id: i.id, position: { column: id, sort: idx + 1, width: autoWidth ? 'auto' : width } })
      })
    })
    const res = await modDashboardSettingPosition(reqBody)
    if (res?.status === 200) {
      message.success(ln_t('message.success.saveCurrentView'))
      goBack()
    }
    setIsLoading(false)
  }

  // ----- View Position & Width Setting ----- //
  const handleDragEnd = ({ source, destination }) => {
    if (!destination) return
    const srcId = source.droppableId
    const destId = destination.droppableId
    if (srcId !== destId) {
      const srcItems = [...columns[srcId].items]
      const destItems = [...columns[destId].items]
      const [removed] = srcItems.splice(source.index, 1)
      destItems.splice(destination.index, 0, removed)
      return setColumns({ ...columns, [srcId]: { ...columns[srcId], items: srcItems }, [destId]: { ...columns[destId], items: destItems } })
    }
    const copiedItems = [...columns[srcId].items]
    const [removed] = copiedItems.splice(source.index, 1)
    copiedItems.splice(destination.index, 0, removed)
    setColumns({ ...columns, [srcId]: { ...columns[srcId], items: copiedItems } })
  }

  const handleChangeWidth = (id, width) => setColumns({ ...columns, [id]: { ...columns[id], width: width < minColWidth ? minColWidth : width } })
  const handleChangeAutoWidth = (id, checked) => setColumns({ ...columns, [id]: { ...columns[id], autoWidth: checked } })

  // ----- Init Setup ----- //
  const setup = settingData => {
    setupContentFunc(settingData)
    const temp = { ...initCol }
    settingData?.forEach(i => {
      const { column, sort } = prevSetting?.[i?.id] || i?.position || {}
      temp[`${column}`] = { ...temp[`${column}`], [sort]: i }
    })
    const newCol = {}
    Object.entries(temp)?.forEach(([key, value]) => {
      const { autoWidth, width } = prevWidth[key] || {}
      const items = Object.values(value)?.flat()
      const savedWidth = items.find(i => i?.position?.width)?.position?.width || 'auto'
      newCol[`${key}`] = {
        items,
        autoWidth: autoWidth !== undefined ? autoWidth : savedWidth === 'auto',
        width: width || savedWidth === 'auto' ? initColWidth : savedWidth
      }
    })
    setColumns(newCol)
    setIsLoading(false)
  }
  const getSetting = async () => {
    setIsLoading(true)
    if (useSetting) return setup(setting || [])
    const catParams = type === GROUP ? { groupId: data?.group?.id } : { brandId: data?.id || data?.brandId }
    const res = await fetchDashboardSetting({ ...catParams, category: type, maxResults: 9999, status: 1 })
    if (res?.status === 200) return setup(res?.data)
    setIsLoading(false)
  }
  const getDataSource = async () => {
    const src = await fetchDataSource({ maxResults: 999, includes: 'brands', groupId: data?.group?.id, status: 'done' })
    if (src?.status === 200) setDataSrcList(type === GROUP ? src?.data : src?.data?.filter(i => i.brands.some(j => j.id === data?.id)))
    const srcType = await fetchSources({ maxResults: 999 })
    if (srcType?.status === 200) setDataSrcTypes(srcType?.data)
  }
  useEffect(() => {
    if (!data) return goBack()
    getSetting()
    if (adminMode) getDataSource()
  }, [type, fixedData])

  // ----- Add / Edit / Delete Items ----- //
  const successCallback = () => {
    setIsLoading(true)
    const prevSetting = {}
    const prevWidth = {}
    Object.entries(columns)?.forEach(([colId, val]) => {
      prevWidth[colId] = { autoWidth: val.autoWidth, width: val.width }
      val.items.forEach((item, idx) => { prevSetting[item.id] = { column: colId, sort: idx + 1 } })
    })
    const newState = { ...state, prevSetting, prevWidth }
    window.location.reload()
    navigate('', { state: newState })
    setIsLoading(false)
  }
  const handleChangeItem = (columnId, _type, mode, editData) => {
    const ruleType = _type === FIRSTPARTY_TAXONOMY || _type === THIRDPARTY_TAXONOMY ? TAGS : _type
    const sort = mode === 'add' ? (columns[columnId]?.items?.length ? Math.max(...columns[columnId]?.items?.map(i => i.position.sort)) : 0) + 1 : editData?.position?.sort
    const modeParams = mode === 'add' ? { addMode: true } : { editMode: true, editData }
    setModalData({ ...component[ruleType], ...modeParams, type: ruleType, columnId, sort, category: type, brandId: data?.id, groupId: data?.group?.id, successCallback })
    setShowModal(true)
  }
  const handleDelete = async ({ title, id, category }) => {
    Modal.confirm({
      title: `${ln_t('util.delete')} ${title}`,
      content: `${ln_t('message.confirm.delete')} ${title}?`,
      onOk: async () => {
        const conf = { ...config.default, ...config[category] }
        const result = await conf.apis.delApi(id, data?.id)
        if (result?.status === 204) {
          message.success(ln_t('message.success.delete'))
          successCallback()
        }
      }
    })
  }
  const handleRefresh = async data => {
    const { id, categoryDetail, groupId, brandId, tableName } = data || {}
    setRefreshLoading(id)
    const res = await refreshDashboardData({ id, categoryDetail, groupId, brandId, tableName })
    if (res?.status === 200) setContent({ ...content, [id]: res?.data })
    setRefreshLoading(false)
  }
  const actions = { handleEdit: handleChangeItem, handleDelete, handleRefresh }
  const passProps = { settingMode: adminMode, actions, useFakeData, allowRefresh, refreshLoading }
  const options = {
    [CONSUMER]: [PII, PIECHART, BARCHART, LINECHART, SUMMARY, TIMELINE, TAGS, CUSTOM],
    [GROUP]: [PIECHART, BARCHART, LINECHART, SUMMARY, MAP, SANKEY],
    [BRAND]: [PIECHART, BARCHART, LINECHART, SUMMARY, MAP, SANKEY],
    [SEGMENT]: [PIECHART, BARCHART, LINECHART, MAP]
  }
  const component = {
    [PROFILE]: { label: ln_t('dashboard.profile'), component: data => <Profile data={data} {...passProps} /> },
    [PII]: { label: ln_t('dashboard.pii'), component: data => <PIIComponent data={data} {...passProps} /> },
    [FOOTPRINT]: { label: ln_t('dashboard.footprint'), component: data => <Footprint data={data} {...passProps} /> },
    [TIMELINE]: { label: ln_t('dashboard.timeline'), component: data => <Footprint data={data} {...passProps} /> },
    [PIECHART]: { label: ln_t('dashboard.pieChart'), component: data => <PieChart data={data} {...passProps} /> },
    [BARCHART]: { label: ln_t('dashboard.barChart'), component: data => <ColumnChart data={data} {...passProps} /> },
    [LINECHART]: { label: ln_t('dashboard.lineChart'), component: data => <LineChart data={data} {...passProps} /> },
    [SUMMARY]: { label: ln_t('dashboard.summary'), component: data => <Summary data={data} {...passProps} /> },
    [MAP]: { label: ln_t('dashboard.map'), component: data => <TaiwanMap data={data} {...passProps} /> },
    [SANKEY]: { label: ln_t('dashboard.sankey'), component: data => <SankeyChart data={data} {...passProps} /> },
    [CUSTOM]: { label: ln_t('dashboard.customBlock'), component: data => <PIIComponent data={data} {...passProps} /> },
    [TAGS]: { label: ln_t('dashboard.tags'), component: data => <PIIComponent data={data} {...passProps} /> }
  }
  const menu = options[type]?.map(i => ({ key: i, label: component[i]?.label }))
  const statusMapping = {
    error: { title: ln_t('dashboard.error'), icon: <CloseOutlined style={{ color: '#ff4d4f' }} /> },
    'no data': { title: ln_t('dashboard.noData'), icon: <InboxOutlined style={{ color: 'rgba(0,0,0,.3)' }} /> },
    done: { title: ln_t('dashboard.noData'), icon: <InboxOutlined style={{ color: 'rgba(0,0,0,.3)' }} /> },
    empty: { title: ln_t('dashboard.noData'), icon: <InboxOutlined style={{ color: 'rgba(0,0,0,.3)' }} /> },
    calculating: { title: ln_t('dashboard.calculating'), icon: <LoadingOutlined /> },
    other: { title: ln_t('dashboard.waitingForCalculation'), icon: <LoadingOutlined /> }
  }

  return (
    <div style={{ width: '100%' }}>
      {isLoading ? <LoadingWrapper><Spin style={{ marginTop: '240px' }} /></LoadingWrapper> : ''}
      <ConfigProvider theme={{ token: { colorPrimary: 'rgb(71, 182, 175)', colorPrimaryHover: 'rgba(71, 182, 175,.8)' } }}>
        {noHeader
          ? ''
          : <>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex' }}>
                <div>
                  <BackButton style={{ paddingBottom: '8px' }}>
                    <ArrowLeftOutlined onClick={goBack} /> {conf[type].title} {adminMode ? ln_t('dashboard.setting') : ln_t('consumer.detail')}
                  </BackButton>
                  {adminMode ? <Description>{conf[type].desc}</Description> : ''}
                </div>
                {adminMode
                  ? <div style={{ marginTop: '10px' }}><Tag color="geekblue">{ln_t('dashboard.notActualData')}</Tag></div>
                  : ''}
              </div>
              {adminMode ? <Button type="primary" style={{ margin: 'auto 8px' }} onClick={() => handleSaveView()}>{ln_t('dashboard.saveCurrentView')}</Button> : ''}
            </div>
          </>}
        {type === BRAND && <SiteChart {...passProps} />}
        <DashboardWrapper adminMode={adminMode} noDragAndDrop={noDragAndDrop}>
          <DragDropContext onDragEnd={e => handleDragEnd(e)}>
            {Object.entries(columns)?.map(([id, column]) => (
              <Droppable droppableId={id} key={id}>
                {({ droppableProps, innerRef }, snapshot) => (
                  <DashboardColumn {...droppableProps} ref={innerRef} drag={snapshot.isDraggingOver} width={column?.autoWidth ? undefined : `${column?.width}px`}>
                    <>
                      {adminMode
                        ? <WidthSettingWrapper>
                            <div>{ln_t('dashboard.width')}:</div>
                            <Tooltip title={column?.autoWidth ? '' : ln_t('dashboard.pressEnterToChangeWidth')}>
                              <InputNumber disabled={column?.autoWidth} value={column?.width} size="small" min={minColWidth} precision={0} onPressEnter={e => handleChangeWidth(id, e.target.value)} />
                            </Tooltip>
                            <div style={{ display: 'flex', justifyContent: 'space-between', flex: 1 }}>
                              <div><Checkbox checked={column?.autoWidth} onChange={e => handleChangeAutoWidth(id, e.target.checked)}>
                                {ln_t('dashboard.fill')}
                              </Checkbox></div>
                              <Dropdown placement="bottomRight" menu={{ items: menu, onClick: e => handleChangeItem(id, e?.key, 'add') }} trigger={['click']}>
                                <Button size="small" type="primary" style={{ marginLeft: 'auto' }}>+ {ln_t('util.add')}</Button>
                              </Dropdown>
                            </div>
                          </WidthSettingWrapper>
                        : ''}
                      {column?.items?.map((item, idx) => (
                        <Draggable draggableId={`${item?.id}`} key={item?.id} index={idx} isDragDisabled={noDragAndDrop}>
                          {({ innerRef, draggableProps, dragHandleProps }, snapshot) => {
                            const { data, status, updatedAt } = content?.[item?.id] || {}
                            const basedOnStatus = statusMapping[status?.toLowerCase()] || statusMapping.other
                            const updateInfo = updatedAt && status ? <UpdatedAtWrapper>{ln_t('dashboard.updatedAt')}: {dayjs(updatedAt).format('YYYY/MM/DD HH:mm')}</UpdatedAtWrapper> : ''
                            const itemProps = { ...item, colId: id, content: content?.[item?.id], updateInfo }
                            return <DashboardItemWrapper ref={innerRef} {...draggableProps} {...dragHandleProps} drag={snapshot?.isDragging}>
                              {(data && data?.length) || useFakeData || adminMode || !renderBasedOnStatus
                                ? (component[item?.ruleType] || component[item?.UISetting?.type])?.component(itemProps)
                                : <>
                                    <ChartHeader data={itemProps} {...passProps} />
                                    <EmptyPlaceholder>{basedOnStatus.icon} {basedOnStatus.title}</EmptyPlaceholder>
                                  </>}
                            </DashboardItemWrapper>
                          }}
                        </Draggable>
                      ))}
                    </>
                  </DashboardColumn>
                )}
              </Droppable>
            ))}
          </DragDropContext>
        </DashboardWrapper>

        <SettingModal data={modalData} show={showModal} setShow={setShowModal} dataSrcList={dataSrcList} dataSrcTypes={dataSrcTypes} />
      </ConfigProvider>
    </div>
  )
}

export default DashboardSetting
